import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Clock, UtensilsCrossed, Phone, FileText } from 'lucide-react'
import { SectionHeading } from '@/components/shared/SectionHeading'
import { ParallaxImage } from '@/components/shared/ParallaxImage'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CONTACT, SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

const menuHighlights = [
  { emoji: '🥞', es: 'Desayuno Típico Hondureño', en: 'Traditional Honduran Breakfast' },
  { emoji: '🥩', es: 'Carne Asada con Chimol', en: 'Grilled Beef with Chimol' },
  { emoji: '🐟', es: 'Pescado Frito con Tajadas', en: 'Fried Fish with Plantain Chips' },
  { emoji: '🍉', es: 'Ensalada de Frutas Frescas', en: 'Fresh Fruit Salad' },
  { emoji: '🌮', es: 'Tacos con Salsa Fresca', en: 'Tacos with Fresh Salsa' },
  { emoji: '🍳', es: 'Desayuno Buffet Cortesía', en: 'Complimentary Breakfast Buffet' },
  { emoji: '☕', es: 'Café Espresso Hondureño', en: 'Honduran Espresso Coffee' },
  { emoji: '🦐', es: 'Platillos de Mariscos', en: 'Seafood Dishes' },
]

const breakfastPhotos = [
  { src: '/images/restaurant/breakfast-fruit.webp', alt: 'Fresh Fruit' },
  { src: '/images/restaurant/breakfast-plantains.webp', alt: 'Fried Plantains' },
  { src: '/images/restaurant/breakfast-meats.webp', alt: 'Cold Cuts' },
  { src: '/images/restaurant/breakfast-waffles.webp', alt: 'Waffles' },
  { src: '/images/restaurant/breakfast-scrambled-eggs.webp', alt: 'Scrambled Eggs' },
  { src: '/images/restaurant/breakfast-fried-eggs.webp', alt: 'Fried Eggs' },
  { src: '/images/restaurant/breakfast-cereal-station.webp', alt: 'Cereal Station' },
  { src: '/images/restaurant/breakfast-buffet-service.webp', alt: 'Buffet Service' },
  { src: '/images/restaurant/breakfast-waffle-plate.webp', alt: 'Waffle Plate' },
  { src: '/images/restaurant/breakfast-tipico-plate.webp', alt: 'Honduran Breakfast' },
  { src: '/images/restaurant/infused-water-poolside.webp', alt: 'Infused Water' },
  { src: '/images/restaurant/breakfast-tipico-full.webp', alt: 'Full Honduran Breakfast' },
  { src: '/images/restaurant/breakfast-fruit-plate.webp', alt: 'Fresh Fruit Plate' },
  { src: '/images/restaurant/carne-asada-plate.webp', alt: 'Carne Asada' },
  { src: '/images/restaurant/grilled-chicken-poolside.webp', alt: 'Grilled Chicken' },
  { src: '/images/restaurant/tacos-poolside.webp', alt: 'Tacos' },
]

export function RestaurantPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language.startsWith('en') ? 'en' : 'es'

  return (
    <>
      <Helmet>
        <title>{`${t('restaurant.title')} | ${SITE_NAME}`}</title>
        <meta name="description" content={t('restaurant.subtitle') as string} />
      </Helmet>

      <div data-hero className="relative h-[60vh] min-h-[400px]">
        <ParallaxImage
          src={getImageUrl('/images/restaurant/la-picota-night.webp')}
          alt={t('restaurant.title')}
          className="h-full w-full"
          overlay
          overlayOpacity={0.5}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="font-heading text-4xl font-bold md:text-5xl lg:text-6xl">
              {t('restaurant.title')}
            </h1>
            <p className="mt-4 text-lg text-white/90 md:text-xl">{t('restaurant.subtitle')}</p>
          </div>
        </div>
      </div>

      <section className="bg-cream-light py-20">
        <div className="mx-auto max-w-7xl px-4">
          <AnimatedSection className="mx-auto mb-16 max-w-3xl text-center">
            <p className="text-lg leading-relaxed text-stone">{t('restaurant.description')}</p>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <Card className="mx-auto mb-16 max-w-lg">
              <CardContent className="p-8">
                <div className="mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-terracotta" />
                  <h2 className="font-heading text-2xl font-bold text-wood-dark">
                    {t('restaurant.hours_title')}
                  </h2>
                </div>
                <div className="space-y-3 text-stone">
                  <p>{t('restaurant.breakfast')}</p>
                  <p>{t('restaurant.lunch')}</p>
                  <p>{t('restaurant.dinner')}</p>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <SectionHeading
              tagline={t('restaurant.menu_highlights')}
              title={t('restaurant.menu_highlights')}
            />
            <div className="mb-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {menuHighlights.map((item, i) => (
                <Card key={i} className="transition-shadow hover:shadow-md">
                  <CardContent className="flex items-center gap-4 p-5">
                    <span className="text-3xl">{item.emoji}</span>
                    <span className="font-medium text-wood-dark">
                      {locale === 'es' ? item.es : item.en}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="mb-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {breakfastPhotos.map((p, i) => (
                <div
                  key={i}
                  className="group relative aspect-square overflow-hidden rounded-lg"
                >
                  <img
                    src={getImageUrl(p.src)}
                    alt={p.alt}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <div className="rounded-2xl bg-wood-dark p-12 text-center text-white">
              <UtensilsCrossed className="mx-auto mb-4 h-10 w-10 text-gold" />
              <h2 className="mb-4 font-heading text-3xl font-bold">{t('restaurant.reserve')}</h2>
              <p className="mb-6 text-white/80">{t('restaurant.subtitle')}</p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a href="/menu-hac.pdf" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-gold text-gold hover:bg-gold hover:text-wood-dark"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    {locale === 'es' ? 'Ver Menú' : 'View Menu'}
                  </Button>
                </a>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white hover:text-wood-dark"
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    {CONTACT.phone}
                  </Button>
                </a>
                <a
                  href="https://wa.me/50494541367?text=Hola%2C%20me%20gustar%C3%ADa%20reservar%20una%20mesa%20en%20el%20Restaurante%20La%20Picota"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-gold text-wood-dark hover:bg-gold/90">
                    {t('restaurant.reserve')}
                  </Button>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
